import React from 'react';
import { Form, FormItem, Input, ResetButton, Select, SubmitButton } from 'formik-antd';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { Col, DatePicker, Row, Space } from 'antd';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import useDatepickerHandler from '../../../../app/hooks/useDatepickerHandler';
import { mkValidationSchema } from './FormFilterDictionary.const';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import { DATE_FORMAT } from '../../../../const/system';



const FormFilterDictionary = ({
  onSubmit,
  onReset,
  filterOptions,
  initialValues,
  isSubmitting,
  formErrors = {},
  showSourceFilter = true,
}) => {
  const intl = useIntl();
  const [ searchQueryParams ] = useSearchParams();
  const multipleSources = searchQueryParams.get('multipleSources') ?? null;

  const { onBlur } = useDatepickerHandler();

  const filterSourceOptions = filterOptions.sources.map((source) => {
    return {
      label: intl.formatMessage({ id: `structures-source-${source.value}` }),
      value: source.value,
    };
  });

  return (
    <Formik
      enableReinitialize
      validationSchema={mkValidationSchema(multipleSources)}
      isSubmitting={isSubmitting}
      initialValues={initialValues}
      initialErrors={formErrors}
      onSubmit={onSubmit}
      onReset={onReset}
    >
      {({ isValid, setFieldValue, dirty, values, setFieldTouched }) => {
        return (
          <Form layout="vertical" className='w-full'>
            <Row gutter={16}>
              <Col span={24}>
                <FormItem
                  className='ant-form-item-col'
                  name="name"
                  label={<IntlMessages id="ui-general-name" />}
                >
                  <Input
                    name='name'
                    placeholder={intl.formatMessage({ id: 'ui-general-name' })}
                  />
                </FormItem>
              </Col>
              {showSourceFilter && (
                <Col span={24}>
                  <FormItem
                    className='ant-form-item-col'
                    name="source"
                    label={<IntlMessages id="ui-dictionaries-source-field" />}
                  >
                    <Select
                      name="source"
                      mode="multiple"
                      allowClear
                      showSearch
                      options={filterSourceOptions}
                      placeholder={<IntlMessages id="form-dictionaries-source-placeholder" />}
                    />
                  </FormItem>
                </Col>
              )}
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <FormItem
                  className='ant-form-item-col'
                  name="field"
                  label={<IntlMessages id="form-dictionaries-field-field" />}
                >
                  <Select
                    defaultValue={initialValues.field}
                    allowClear
                    name="field"
                    options={filterOptions.fields}
                    placeholder={<IntlMessages id="form-dictionaries-field-placeholder" />}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem
                  className='ant-form-item-col'
                  name="field_value"
                  label={<IntlMessages id="form-dictionaries-field-value-field" />}
                >
                  <Input
                    name='field_value'
                    placeholder={intl.formatMessage({ id: 'form-dictionaries-field-value-field' })}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <FormItem
                  className='ant-form-item-col'
                  name="version"
                  label={<IntlMessages id="form-dictionaries-structure-field" />}
                >
                  <Select
                    allowClear
                    name="version"
                    options={filterOptions.versions}
                    placeholder={<IntlMessages id="form-dictionaries-structure-placeholder" />}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <FormItem
                  className='ant-form-item-col'
                  name="created_from"
                  label={<IntlMessages id="ui-general-created-from" />}
                >
                  <DatePicker
                    name='created_from'
                    format={DATE_FORMAT}
                    value={values?.created_from ? moment(values.created_from) : null}
                    onChange={async (date, dateString) => {
                      await setFieldValue('created_from', dateString);
                    }}
                    onBlur={async ({ target }) => {
                      await setFieldTouched('created_from', true);
                      await onBlur('created_from', target.value, setFieldValue);
                    }}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem
                  className='ant-form-item-col'
                  name="created_to"
                  label={<IntlMessages id="ui-general-created-to" />}
                >
                  <DatePicker
                    name='created_to'
                    value={values?.created_to ? moment(values.created_to) : null}
                    onChange={async (date, dateString) => {
                      await setFieldValue('created_to', dateString ? `${dateString}T23:59:59` : dateString);
                    }}
                    onBlur={async ({ target }) => {
                      await setFieldTouched('created_to', true);
                      await onBlur('created_to', target.value, setFieldValue);
                    }}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='end'>
              <Col>
                <Space>
                  <ResetButton disabled={false} loading={isSubmitting}>
                    <IntlMessages id='ui-general-reset' />
                  </ResetButton>
                  <SubmitButton
                    loading={isSubmitting}
                    disabled={!isValid || !dirty}
                  >
                    <IntlMessages id='ui-general-apply' />
                  </SubmitButton>
                </Space>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

FormFilterDictionary.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    field: PropTypes.string,
    created_from: PropTypes.string,
    created_to: PropTypes.string,
  }).isRequired,
  filterOptions: PropTypes.shape({
    fields: PropTypes.array.isRequired,
    sources: PropTypes.array.isRequired,
    versions: PropTypes.array.isRequired,
  }).isRequired,
  formErrors: PropTypes.object,
  isSubmitting: PropTypes.bool.isRequired,
  showSourceFilter: PropTypes.bool,
};

export default FormFilterDictionary;

